import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { ListChecks, X } from "lucide-react";
import { paths } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { useUiPrefs } from "../hooks/useUiPrefs";
import { TodoCard } from "./TodoCard";

// The to-do list one tap away on every page: a small corner button with how
// many are open for you; it opens your list over the page. Hide the button if
// it's in the way (bring it back from the dashboard's To-dos).

export function TodoDock({ tenantId, uid }: { tenantId: string; uid: string }) {
  const { prefs, loaded, save } = useUiPrefs();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [overdue, setOverdue] = useState(false);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantTodos(tenantId)), where("assignedUid", "==", uid), where("done", "==", false)),
        (s) => {
          setCount(s.size);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          setOverdue(s.docs.some((d) => {
            const due = d.get("dueAt") as number | null;
            return due !== null && due < today.getTime() + 86_400_000;
          }));
        },
        () => setCount(0),
      ),
    [tenantId, uid],
  );
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!loaded || prefs.todoDock === "hidden") return null;
  return (
    <>
      {open ? (
        <div
          className="fixed z-30 w-[min(400px,calc(100vw-2rem))] overflow-y-auto rounded-xl shadow-2xl shadow-ink/20"
          style={{ right: "max(1rem, env(safe-area-inset-right, 0px))", bottom: "calc(5.5rem + env(safe-area-inset-bottom, 0px))", maxHeight: "70vh" }}
          role="dialog"
          aria-label="My to-dos"
        >
          <div className="flex items-center justify-between rounded-t-xl bg-ink px-3 py-1.5 text-xs text-white">
            <button className="text-white/70 hover:text-white" onClick={() => { save({ todoDock: "hidden" }); setOpen(false); }}>
              Hide this button
            </button>
            <button aria-label="Close to-dos" className="text-white/80 hover:text-white" onClick={() => setOpen(false)}>
              <X size={15} />
            </button>
          </div>
          <TodoCard tenantId={tenantId} title="My to-dos" />
        </div>
      ) : null}
      <button
        className={cn(
          "fixed z-30 flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold shadow-lg transition-colors",
          open ? "bg-ink text-white" : "bg-white text-ink ring-1 ring-steel/25 hover:ring-brandorange",
        )}
        style={{ right: "max(1rem, env(safe-area-inset-right, 0px))", bottom: "calc(4.5rem + env(safe-area-inset-bottom, 0px))" }}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`To-dos: ${count} open`}
      >
        <ListChecks size={16} />
        <span className="hidden sm:inline">To-dos</span>
        {count ? <span className={cn("rounded-full px-1.5 text-[11px] text-white", overdue ? "bg-danger" : "bg-brandorange")}>{count}</span> : null}
      </button>
    </>
  );
}
